import React, { useState, useEffect } from 'react'
import { 
	Box,
	Typography,
	Stack,
	useMediaQuery,
	useTheme,
	IconButton, 
	Card, 
	CardContent, 
	Button,
	Chip,
	Fab,
	CircularProgress,
	Alert,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import { apiBase } from '../../services/apiBase'
import { api } from '../../utils/api'
import UserLayout from '../../layouts/UserLayout'
import { useNavigate } from 'react-router-dom'

function Product() {
	const [products, setProducts] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [screenWidth, setScreenWidth] = useState(window.innerWidth)
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
	const navigate = useNavigate()

	// Tính toán số cột và kích thước dựa trên màn hình
	const calculateGridLayout = () => {
		const containerWidth = screenWidth - 20 // Trừ đi padding 10px 2 bên
		const minProductWidth = 140 // Kích thước tối thiểu của sản phẩm
		const spacing = 8 // Khoảng cách giữa các sản phẩm (8px = 1 theme spacing)
		
		// Tính số cột tối đa có thể hiển thị
		const maxColumns = Math.floor((containerWidth + spacing) / (minProductWidth + spacing))
		
		// Giới hạn số cột từ 2-8 để đảm bảo UX tốt
		const optimalColumns = Math.max(2, Math.min(8, maxColumns))
		
		// Tính kích thước thực tế của mỗi sản phẩm
		const actualProductWidth = (containerWidth - (optimalColumns - 1) * spacing) / optimalColumns
		
		return {
			columns: optimalColumns,
			productWidth: actualProductWidth,
			spacing: spacing
		}
	}

	// Theo dõi thay đổi kích thước màn hình
	useEffect(() => {
		const handleResize = () => {
			setScreenWidth(window.innerWidth)
		}

		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [])

	useEffect(() => {
		const fetchProducts = async () => {
			try {
				setError(null)
				const response = await apiBase.get(api.ZALO_MINI_APP_PRODUCT_LIST)

				// Kiểm tra và set dữ liệu đúng cấu trúc
				const productsData = response || []
				setProducts(productsData)
			} catch (error) {
				console.error('Error fetching products:', error)
				setError('Không thể tải danh sách sản phẩm. Vui lòng thử lại.')
				setProducts([])
			} finally {
				setLoading(false)
			}
		}

		fetchProducts()
	}, [])

	const handleAddProduct = () => {
		navigate('/admin/zalo-mini-app/product/create')
	}

	const handleProductClick = (product) => {
		navigate(`/admin/zalo-mini-app/product/${product.id}`, {
			state: { product },
		})
	}

	// Lấy layout tối ưu
	const gridLayout = calculateGridLayout()

	if (loading) {
		return (
			<UserLayout>
				<Box sx={{ 
					display: 'flex', 
					justifyContent: 'center', 
					alignItems: 'center', 
					minHeight: '60vh',
					flexDirection: 'column',
					gap: 2
				}}>
					<CircularProgress size={60} sx={{ color: 'primary.main' }} />
					<Typography variant="h6" color="text.secondary">
						Đang tải sản phẩm...
					</Typography>
				</Box>
			</UserLayout>
		)
	}

	return (
		<UserLayout>
			<Box sx={{ py: 0 }}>
				{/* Modern Header */}
				<Box
					sx={{
						mb: 4,
						position: 'relative',
					}}>
					<Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
						{/* Top Bar */}
						<Box sx={{ 
							display: 'flex', 
							alignItems: 'center', 
							mb: 3,
							gap: 2
						}}>
							<IconButton 
								edge='start' 
								color='primary' 
								onClick={() => navigate(-1)}
								sx={{
									background: 'rgba(0, 0, 0, 0.04)',
									'&:hover': {
										background: 'rgba(0, 0, 0, 0.08)',
									}
								}}>
								<ArrowBackIcon />
							</IconButton>
							<Typography variant='h4' sx={{ flex: 1, fontWeight: 700, color: 'text.primary' }}>
								Sản phẩm
							</Typography>
						</Box>

						{/* Stats and Actions */}
						<Box sx={{ 
							display: 'flex', 
							justifyContent: 'space-between', 
							alignItems: 'center',
							flexWrap: 'wrap',
							gap: 2
						}}>
							<Box>
								<Typography variant='h6' sx={{ opacity: 0.7, mb: 0.5, color: 'text.secondary' }}>
									Tổng số sản phẩm
								</Typography>
								<Typography variant='h3' sx={{ fontWeight: 800, color: 'text.primary' }}>
									{products.length}
								</Typography>
							</Box>
							<Button
								variant="contained"
								startIcon={<AddIcon />}
								onClick={handleAddProduct}
								sx={{
									px: 3,
									py: 1.5,
									borderRadius: '12px',
									fontWeight: 600,
									'&:hover': {
										transform: 'translateY(-2px)',
									},
									transition: 'all 0.3s ease',
								}}>
								Thêm sản phẩm
							</Button>
						</Box>
					</Box>
				</Box>

				{/* Error Alert */}
				{error && (
					<Alert severity="error" sx={{ mb: 3, borderRadius: '12px', mx: 1.25 }}>
						{error}
					</Alert>
				)}

				{/* Products Grid */}
				<Box sx={{ px: 1.25 }}>
					{products.length === 0 ? (
						<Box sx={{ 
							textAlign: 'center', 
							py: 8,
							background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
							borderRadius: '24px',
							mx: 1.25
						}}>
							<ShoppingCartIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
							<Typography variant="h5" color="text.secondary" sx={{ mb: 1 }}>
								Chưa có sản phẩm nào
							</Typography>
							<Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
								Bắt đầu bằng cách thêm sản phẩm đầu tiên
							</Typography>
							<Button
								variant="contained"
								startIcon={<AddIcon />}
								onClick={handleAddProduct}
								sx={{
									borderRadius: '12px',
									px: 4,
									py: 1.5,
									fontWeight: 600,
								}}>
								Thêm sản phẩm đầu tiên
							</Button>
						</Box>
					) : (
						<Box sx={{ 
							display: 'grid',
							gridTemplateColumns: `repeat(${gridLayout.columns}, 1fr)`,
							gap: `${gridLayout.spacing}px`,
							rowGap: `${gridLayout.spacing * 2}px`,
							width: '100%'
						}}>
							{products.map((product) => (
								<Card
									key={product._id}
									elevation={0}
									onClick={() => handleProductClick(product)}
									sx={{
										cursor: 'pointer',
										borderRadius: '16px',
										border: '1px solid rgba(0, 0, 0, 0.08)',
										background: 'white',
										overflow: 'hidden',
										transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
										height: '100%',
										display: 'flex',
										flexDirection: 'column',
										width: '100%',
										minHeight: '280px',
										'&:hover': {
											transform: 'translateY(-8px)',
											boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
											borderColor: 'primary.main',
										},
									}}>
									{/* Product Image */}
									<Box
										sx={{
											position: 'relative',
											height: { xs: 180, md: 160 },
											overflow: 'hidden',
											flexShrink: 0,
										}}>
										<Box
											component='img'
											src={product.image}
											alt={product.name}
											sx={{
												width: '100%',
												height: '100%',
												objectFit: 'cover',
												transition: 'transform 0.3s ease',
												'&:hover': {
													transform: 'scale(1.05)',
												},
											}}
										/>
										
										{/* Sale Badge */}
										{product.on_sale === 1 && (
											<Chip
												label="GIẢM GIÁ"
												sx={{
													position: 'absolute',
													top: 12,
													right: 12,
													background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%)',
													color: 'white',
													fontWeight: 600,
													fontSize: '0.75rem',
													height: 24,
												}}
											/>
										)}
									</Box>

									{/* Product Info */}
									<CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
										<Typography
											variant='h6'
											sx={{
												fontWeight: 600,
												mb: 1,
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												display: '-webkit-box',
												WebkitLineClamp: 2,
												WebkitBoxOrient: 'vertical',
												lineHeight: 1.3,
												minHeight: '2.6em',
												fontSize: '0.9rem',
											}}>
											{product.name}
										</Typography>

										{/* Price Section */}
										<Box sx={{ mt: 'auto' }}>
											{product.on_sale === 1 ? (
												<Stack direction='row' spacing={1} alignItems='center' flexWrap="wrap">
													<Typography
														variant='body2'
														sx={{
															textDecoration: 'line-through',
															color: 'text.secondary',
															fontSize: '0.8rem',
														}}>
														{product.price.toLocaleString()}đ
													</Typography>
													<Typography 
														variant='h6' 
														sx={{ 
															color: '#ff5722',
															fontWeight: 700,
															fontSize: '1rem',
														}}>
														{product.sale_price.toLocaleString()}đ
													</Typography>
												</Stack>
											) : (
												<Typography 
													variant='h6' 
													sx={{ 
														color: 'primary.main',
														fontWeight: 700,
														fontSize: '1rem',
													}}>
													{product.price.toLocaleString()}đ
												</Typography>
											)}
										</Box>
									</CardContent>
								</Card>
							))}
						</Box>
					)}
				</Box>

				{/* Floating Action Button for Mobile */}
				{isMobile && (
					<Fab
						color="primary"
						aria-label="add product"
						onClick={handleAddProduct}
						sx={{
							position: 'fixed',
							bottom: 80,
							right: 16,
							background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
							'&:hover': {
								background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
								transform: 'scale(1.1)',
							},
							transition: 'all 0.3s ease',
						}}>
						<AddIcon />
					</Fab>
				)}
			</Box>
		</UserLayout>
	)
}

export default Product
